import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Card,
	CardHeader,
	CardContent,
	CardFooter,
} from "@/components/ui/card";
import { MessageSquare, ArrowRight, Minimize2 } from "lucide-react";
import ChatMessage from "./ChatMessage";

// Replace this with your actual n8n webhook URL
const N8N_WEBHOOK_URL =
	"https://primary-production-b8f5.up.railway.app/webhook/9388fd57-b064-4265-b82c-4232e62dcbaf";

const LaundroChatbot = () => {
	const [minimized, setMinimized] = useState(true);
	const [messages, setMessages] = useState([
		{
			text: "Hello! 👋 Welcome to Laundro. How can I help you with your laundry needs today?",
			isUser: false,
		},
	]);
	const [inputMessage, setInputMessage] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const messagesEndRef = useRef(null);
	const sessionIdRef = useRef(
		`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
	);

	// Auto-scroll to bottom of messages
	useEffect(() => {
		const messagesEnd = messagesEndRef.current as HTMLDivElement | null;
		if (messagesEnd) {
			messagesEnd.scrollIntoView({ behavior: "smooth" });
		}
	}, [messages]);

	// Save conversation history to local storage
	useEffect(() => {
		localStorage.setItem(
			`laundro_chat_${sessionIdRef.current}`,
			JSON.stringify(messages),
		);
	}, [messages]);

	// Load conversation history from local storage on initial load
	useEffect(() => {
		const savedMessages = localStorage.getItem(
			`laundro_chat_${sessionIdRef.current}`,
		);
		if (savedMessages) {
			try {
				setMessages(JSON.parse(savedMessages));
			} catch (e) {
				console.error("Error parsing saved messages:", e);
			}
		}
	}, []);

	const toggleMinimize = () => {
		setMinimized(!minimized);
	};

	const handleSendMessage = async () => {
		if (!inputMessage.trim()) return;

		// Add user message to the chat
		setMessages((prev) => [...prev, { text: inputMessage, isUser: true }]);

		// Clear the input
		setInputMessage("");

		// Show loading state
		setIsLoading(true);
		setMessages((prev) => [
			...prev,
			{ text: "...", isUser: false, isLoading: true },
		]);

		try {
			// Send to n8n webhook
			const response = await fetch(N8N_WEBHOOK_URL, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					message: inputMessage,
					sessionId: sessionIdRef.current,
					chatInput: inputMessage,
				}),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();

			// Remove loading message and add real response
			setMessages((prev) => {
				const filtered = prev.filter((msg) => !("isLoading" in msg));
				return [
					...filtered,
					{
						text:
							data.output ||
							data.response ||
							"Sorry, I couldn't process your request.",
						isUser: false,
					},
				];
			});
		} catch (error) {
			// Remove loading message and add error message
			setMessages((prev) => {
				const filtered = prev.filter((msg) => !("isLoading" in msg));
				return [
					...filtered,
					{
						text: "Sorry, I'm having trouble connecting to our systems. Please try again later or contact us directly.",
						isUser: false,
					},
				];
			});
			console.error("Error:", error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSendMessage();
		}
	};

	return (
		<>
			{/* Chat bubble button (visible when chat is minimized) */}
			{minimized && (
				<Button
					className="fixed bottom-5 right-5 h-14 w-14 rounded-full z-50 p-0 bg-primary hover:bg-primary/90 text-white"
					onClick={toggleMinimize}
				>
					<MessageSquare size={24} />
				</Button>
			)}

			{/* Chat container */}
			<Card
				className={`fixed bottom-5 right-5 w-80 ${
					minimized ? "h-0 opacity-0" : "h-96 opacity-100"
				} flex flex-col shadow-lg transition-all duration-300 ease-in-out z-40 bg-white/70`}
			>
				<CardHeader
					className="p-3 bg-primary hover:bg-primary/70 text-white font-bold flex flex-row justify-between items-center cursor-pointer"
					onClick={toggleMinimize}
				>
					<div>Laundro Assistant</div>
					<Button
						variant="ghost"
						size="icon"
						className="h-6 w-6 p-0 text-white hover:bg-primary/90"
					>
						<Minimize2 size={16} />
					</Button>
				</CardHeader>

				{!minimized && (
					<>
						<CardContent className="flex-grow p-3 overflow-y-auto bg-white/70">
							{messages.map((msg, index) => (
								<ChatMessage
									key={`msg-${index}-${msg.text.substring(0, 10)}`}
									message={msg.text}
									isUser={msg.isUser}
								/>
							))}
							<div ref={messagesEndRef} />
						</CardContent>

						<CardFooter className="p-3 border-t bg-white/70">
							<div className="flex w-full items-center gap-2">
								<Input
									type="text"
									placeholder="Type your message..."
									value={inputMessage}
									onChange={(e) => setInputMessage(e.target.value)}
									onKeyPress={handleKeyPress}
									disabled={isLoading}
									className="rounded-full"
								/>
								<Button
									onClick={handleSendMessage}
									disabled={isLoading}
									size="icon"
									className="h-9 w-9 rounded-full bg-primary hover:bg-primary/70"
								>
									<ArrowRight size={18} color="white" />
								</Button>
							</div>
						</CardFooter>
					</>
				)}
			</Card>
		</>
	);
};

export default LaundroChatbot;
